// FLAG:useDocuAi — builds the ReceiptEdit review-mode data object from a document (file info).
// Extracted verbatim from document-invoice-review-page.tsx so the drawer-embedded review tab
// (document-invoice-review-drawer-tab.tsx) and the full review page share one source of truth.
import { user } from 'ampersand-app'
import { checkModuleAccess } from '@/modules'
import { experimentalFeatures, receiptStates, SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import { resolveUndecidedBookingTarget } from '@/modules/receipt/helpers/invoice-review-config'
import {
  applyReviewPayeeToReceiptData,
  createReviewPayeeState,
  getReviewPayeeDisplayName,
  ReviewPayeeInput,
  ReviewPayeeState,
} from '@/modules/receipt/review/review-payee-state'

const getPartyAddress = (party, type) => {
  if (type === 'customer' && party.customerDefaultAddress?.billingAddress) {
    return party.customerDefaultAddress.billingAddress
  }
  if (party.defaultBillingAddressId) {
    return party.addresses?.find(address => address.id === party.defaultBillingAddressId)
  }
  return party.addresses?.find(address => address.isDefault) ?? party.addresses?.[0] ?? {}
}

const buildReviewPayeeFromParty = (party, type): ReviewPayeeState => {
  const address = getPartyAddress(party, type)
  const role = type === 'supplier'
    ? 'supplier'
    : party.kind === 'person' ? 'privateCustomer' : 'companyCustomer'

  return createReviewPayeeState({
    role,
    directoryRef: { type, id: party.id },
    companyName: party.companyName ?? (role === 'privateCustomer' ? '' : party.name),
    companyNameAffix: party.companyNameAffix,
    salutation: party.salutation,
    title: party.title,
    firstName: party.firstName,
    lastName: party.lastName,
    address: {
      street: address.street,
      zipCode: address.zipCode ?? address.zip,
      city: address.city,
      countryIso: address.countryIso,
    },
  })
}

const getSingleExplicitParty = (supplier, customer) => {
  if (supplier?.id && !customer?.id) return { party: supplier, type: 'supplier' }
  if (customer?.id && !supplier?.id) return { party: customer, type: 'customer' }
  return null
}

const buildNewSupplierReviewPayee = (documentInvoiceDetails, ocr) => createReviewPayeeState({
  role: 'supplier',
  companyName: documentInvoiceDetails.accountHolder ?? ocr.payee ?? ocr.payeeName,
  address: {
    street: ocr.payeeAddress?.street ?? ocr.payeeStreet,
    zipCode: ocr.payeeAddress?.zipCode ?? ocr.payeeAddress?.zip ?? ocr.payeeZipCode,
    city: ocr.payeeAddress?.city ?? ocr.payeeCity,
    countryIso: ocr.payeeAddress?.countryIso ?? ocr.payeeCountryIso,
  },
})

// FLAG:useDocuAi — the BUYER as printed on the document (contractToAnalysis' additive `buyer`
// block, re-derived onto metaData.ocr from the stored contract on every read). It seeds the
// contact form when the reviewer says the counterparty is a company/private customer. Null unless the
// document actually named a buyer — we never invent one.
const buildRecognizedBuyerReviewPayee = (ocr) => {
  const buyer = ocr?.buyer
  if (!buyer?.name && !buyer?.city && !buyer?.street) return null

  return createReviewPayeeState({
    role: 'companyCustomer',
    companyName: buyer.name,
    address: {
      street: buyer.street,
      zipCode: buyer.zipCode,
      city: buyer.city,
      countryIso: buyer.countryIso,
    },
  })
}

// FLAG:useDocuAi — has a HUMAN already gone through the details row? `reviewStatus` is written by
// the review save ('partial' | 'complete'); it is absent on a row that was never reviewed and
// 'review' on one the server merely ingested from the analysis (also what a server-side reset
// restores). Only in those two states may a stored blank still be filled from `metaData.ocr` —
// afterwards the stored value IS the reviewer's answer, and an emptied field must stay empty.
const isReviewedDetailsRow = (documentInvoiceDetails) => {
  const reviewStatus = documentInvoiceDetails?.reviewStatus
  return !!reviewStatus && reviewStatus !== 'review'
}

// FLAG:useDocuAi — `stored ?? ocr` refilled a field the reviewer had DELETED (report: the IBAN kept
// coming back after every save). Once the row is reviewed the stored value wins, `null` included.
const resolveReviewedValue = (storedValue, ocrValue, detailsReviewed) => {
  if (detailsReviewed) { return storedValue ?? null }
  return storedValue ?? ocrValue
}

const buildDraftPartyData = (reviewPayee, documentInvoiceDetails, ocr, detailsReviewed) => {
  const displayName = getReviewPayeeDisplayName(reviewPayee)
  return {
    name: displayName,
    kind: reviewPayee.role === 'privateCustomer' ? 'person' : 'company',
    companyName: reviewPayee.companyName,
    companyNameAffix: reviewPayee.companyNameAffix,
    salutation: reviewPayee.salutation,
    title: reviewPayee.title,
    firstName: reviewPayee.firstName,
    lastName: reviewPayee.lastName,
    // FLAG:useDocuAi — the extracted supplier VAT ID (metaData.ocr.payeeVatId, contract
    // vendor.vatId; report 92809b7b). Supplier drafts only: this draft can also be a customer
    // (persisted reviewPayee role), and the VENDOR's VAT ID must never seed a customer.
    // supplier create whitelists vatId, so "Lieferant anlegen" prefills it.
    ...(reviewPayee.role === 'supplier' && ocr.payeeVatId ? { vatId: ocr.payeeVatId } : {}),
    addresses: [{
      ...reviewPayee.address,
      zip: reviewPayee.address.zipCode,
      email: ocr.email,
      phoneNumber: ocr.phoneNumber || ocr.phone1,
      isDefault: true,
      kind: reviewPayee.role === 'privateCustomer' ? 'person' : 'company',
    }],
    bankAccounts: [{
      accountHolder: documentInvoiceDetails.accountHolder ?? displayName,
      iban: resolveReviewedValue(documentInvoiceDetails.iban, ocr.payeeIban, detailsReviewed),
      bic: resolveReviewedValue(documentInvoiceDetails.bic, ocr.payeeBic, detailsReviewed),
      bankInstitute: resolveReviewedValue(documentInvoiceDetails.bankInstitute, ocr.payeeBank, detailsReviewed),
    }],
  }
}

const buildReviewPayeeDraft = (file, documentInvoiceDetails, ocr, detailsReviewed) => {
  let reviewPayee: ReviewPayeeState
  let serializedParty = null

  if (documentInvoiceDetails.reviewPayee?.version === 1) {
    reviewPayee = createReviewPayeeState(documentInvoiceDetails.reviewPayee as ReviewPayeeInput)
  } else {
    const linkedParty = getSingleExplicitParty(file?.supplier, file?.customer)
    const analysedParty = getSingleExplicitParty(ocr?.supplier, ocr?.customer)
    const selectedParty = linkedParty ?? analysedParty

    if (selectedParty) {
      serializedParty = selectedParty
      reviewPayee = buildReviewPayeeFromParty(selectedParty.party, selectedParty.type)
    } else {
      reviewPayee = buildNewSupplierReviewPayee(documentInvoiceDetails, ocr)
    }
  }

  return {
    ...applyReviewPayeeToReceiptData({}, reviewPayee),
    ...(serializedParty?.type === 'supplier' ? { supplier: serializedParty.party } : {}),
    ...(serializedParty?.type === 'customer' ? { customer: serializedParty.party } : {}),
    data: serializedParty?.party ?? buildDraftPartyData(reviewPayee, documentInvoiceDetails, ocr, detailsReviewed),
  }
}

// FLAG:useDocuAi — analysis leaves (metaData.ocr.*) can carry amounts as STRINGS
// (contractToAnalysis extracts priceNet/vatAmount via leafValue, NOT the numeric leafNumber its
// siblings use). A stored null that falls back to ocr would then feed a numeric string to the
// review save, which validateBody rejects (documentInvoiceDetails.priceNet isNumberAllowNull
// accepts only number|null → NO_NUMBER). Coerce so every source is save-valid regardless of the
// leaf's raw type. (This is also what the retired client-side review reset tripped over,
// back when it rebuilt every field from ocr; the reset is a server round-trip now.)
// FLAG:useDocuAi — the booking target the review OPENS with: the reviewer's persisted decision if
// there is one, otherwise the preselection (product decision Ralf, 2026-09-16: „Nur Dokument", so
// nothing is booked before someone actively picks a ledger). The rule itself is pure and unit-tested
// in `resolveUndecidedBookingTarget`; the only input that is not on the file — does the tenant have
// the documents module — is read HERE, from the very source `BookingTargetRadio` renders its options
// from (`checkModuleAccess`, the plain twin of its `useCheckModuleAccess`). Same source on both
// sides is what guarantees the radio can never be preselected with an option it does not draw.
//
// This function is the single seam for all THREE review surfaces: the review page, the drawer tab
// and the expense-embedded review each build their form data through `buildReviewData` and feed the
// result into the same `resolveReviewBookingTarget(… bookingTarget: choice ?? … finalData
// .bookingTarget ?? evidence …)` ladder. Deciding it anywhere else would give one surface a
// different default than the other two.
const resolveInitialBookingTarget = (file, documentInvoiceDetails) =>
  documentInvoiceDetails.bookingTarget ?? resolveUndecidedBookingTarget({
    useDocuAiEnabled: !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi),
    storedBookingTarget: documentInvoiceDetails.bookingTarget,
    receiptUploadAction: file?.metaData?.receiptUploadAction,
    receiptAccountingRestoreTarget: file?.metaData?.receiptAccountingRestoreTarget,
    hasLinkedReceipt: !!(file?.expense || file?.revenue),
    hasDocumentsModule: !!checkModuleAccess(SUBSCRIPTION_MODULES.DOCUMENTS, false),
  })

const toReviewAmount = (value) => {
  if (value === null || value === undefined || value === '') { return null }
  const num = Number(typeof value === 'object' ? value.value : value)
  return Number.isFinite(num) ? num : null
}

export const buildReviewData = async(file) => {
  if (!file) {
    return {
      filesToAssign: [],
      receipts: [],
      bookings: [],
      state: receiptStates.DRAFT,
      date: null,
      deliveryDate: null,
      receiptNumber: '-',
      priceTotal: 0,
      // FLAG:useDocuAi — same reason as below: the payment/assignment drawers put this into
      // `new Decimal(...)` unguarded.
      outstandingAmount: 0,
      positions: [],
      metaData: {},
      readOnly: false,
    }
  }

  const documentInvoiceDetails = file?.documentInvoiceDetails || {}
  const ocr = file?.metaData?.ocr || {}
  const totalAmount = documentInvoiceDetails.totalAmount ?? ocr.priceTotal ?? ocr.priceGross ?? 0
  // Missing analysis dates stay empty. Required-field validation asks the reviewer
  // to supply the printed date; opening the form must never manufacture today.
  const invoiceDate = documentInvoiceDetails.date ?? ocr.date ?? null
  const detailsReviewed = isReviewedDetailsRow(documentInvoiceDetails)
  const payeeDraft = buildReviewPayeeDraft(file, documentInvoiceDetails, ocr, detailsReviewed)
  // FLAG:useDocuAi — what is still OPEN on this document. The payment drawer
  // (PaymentFormModel.fromExpense) and the assignment drawer (buildNormalizedTransaction) both feed
  // this straight into `new Decimal(...)`, which THROWS on undefined — so before this seed both
  // drawers crashed the moment a reviewer tried to record a payment. The linked receipt knows the
  // truth once one exists (partially paid → its remainder); otherwise the whole invoice is open.
  // `Math.abs` because the receipt side carries the open amount unsigned; the drawers apply the
  // ledger's sign themselves.
  const linkedOutstandingAmount = toReviewAmount(
    file?.expense?.outstandingAmount ?? file?.revenue?.outstandingAmount,
  )
  const outstandingAmount = linkedOutstandingAmount ?? Math.abs(toReviewAmount(totalAmount) ?? 0)

  return {
    filesToAssign: [{
      ...file,
      fileId: file.id,
    }],
    receipts: [],
    bookings: [],
    state: receiptStates.DRAFT,
    date: invoiceDate,
    deliveryDate: invoiceDate,
    dueDate: documentInvoiceDetails.paymentDueDate ?? ocr.paymentDueDate,
    payDate: documentInvoiceDetails.payedAt ?? ocr.payedAt,
    receiptNumber: documentInvoiceDetails.invoiceNumber ?? ocr.receiptNumber ?? '-',
    // FLAG:useDocuAi — see resolveReviewedValue: once the details row has been reviewed the stored
    // value is the reviewer's answer. A cleared IBAN/BIC/bank name used to be refilled from the
    // analysis on the next open, so deleting one was impossible.
    iban: resolveReviewedValue(documentInvoiceDetails.iban, ocr.payeeIban, detailsReviewed),
    bic: resolveReviewedValue(documentInvoiceDetails.bic, ocr.payeeBic, detailsReviewed),
    bankInstitute: resolveReviewedValue(documentInvoiceDetails.bankInstitute, ocr.payeeBank, detailsReviewed),
    // FLAG:useDocuAi — every recognized payee IBAN, seeded into the form data because the
    // review component's list is reviewer-owned (correct an entry, delete one) and the curated
    // list is saved back onto documentInvoiceDetails.payeeIbans. `?? null` distinguishes
    // "not seeded" from "the reviewer emptied it".
    payeeIbans: documentInvoiceDetails.payeeIbans ?? null,
    // FLAG:useDocuAi — the ingested per-account bank block (contract 1.7.0). Seeded for the
    // same reason as the list it belongs to: the review component keeps the two INDEX-ALIGNED
    // while the reviewer corrects or deletes a card, so the account rows have to live in the
    // form data rather than being read straight off the ingested details.
    payeeAccounts: documentInvoiceDetails.payeeAccounts ?? null,
    // FLAG:useDocuAi — same for the buyer's debit account: reviewer-owned, so this has to
    // be seeded into the form data rather than read straight off the ingested details.
    debitIbans: documentInvoiceDetails.debitIbans ?? null,
    priceTotal: totalAmount,
    outstandingAmount,
    priceNet: toReviewAmount(documentInvoiceDetails.priceNet ?? ocr.priceNet),
    vatAmount: toReviewAmount(documentInvoiceDetails.vatAmount ?? ocr.vatAmount),
    vat: documentInvoiceDetails.vat ?? ocr.vat,
    positions: documentInvoiceDetails.positions ?? ocr.positions ?? [],
    // FLAG:useDocuAi — whole-invoice document allowance: positionsTotal = Σ positions
    // (pre-adjustment), documentAllowance is signed (positionsTotal − documentAllowance =
    // priceNet). Carried into the form so the position total shows the allowance line
    // (position total → allowance → total). Null when the invoice has no whole-invoice
    // adjustment, so the sum renders exactly as before.
    positionsTotal: documentInvoiceDetails.positionsTotal ?? null,
    documentAllowance: documentInvoiceDetails.documentAllowance ?? null,
    // FLAG:useDocuAi — typed BG-20/21 document allowances/charges (v1.3.0), a first-class
    // editable column. Carried into the form so the allowance/charge line shows reason +
    // rate. Null when the invoice has no whole-invoice adjustment (legacy byte-identical).
    allowanceCharges: documentInvoiceDetails.allowanceCharges ?? null,
    priceKind: documentInvoiceDetails.priceKind,
    // FLAG:useDocuAi — the receipt currency. Until now, the review did not carry it at all, and every
    // amount field hard-coded „€" (CurrencyInput.defaultProps.symbol) — simply wrong on a USD invoice.
    // Only the code, never an exchange rate: nothing is converted here.
    currency: documentInvoiceDetails.currency ?? ocr.currency ?? null,
    payKindId: documentInvoiceDetails.payKindId ?? ocr.payKindId,
    // FLAG:useDocuAi — paid status as printed on the document (display-only) plus its
    // per-field confidences: carried into the form data, so the payment terms area can
    // mark a method field as "needs attention" (red) when the document is paid but no
    // method was chosen. Only ever set for the review flows — plain expense edits never
    // carry these keys, so their behavior is untouched.
    paymentStatus: documentInvoiceDetails.paymentStatus ?? null,
    // FLAG:useDocuAi — seed the extracted open amount so the editable "Offener Betrag"
    // input starts from what DocuAI extracted, matching the other payment fields (without
    // this it always rendered empty even when an open amount was present).
    openAmount: documentInvoiceDetails.openAmount ?? null,
    paymentMethodCode: documentInvoiceDetails.paymentMethodCode ?? null,
    // FLAG:useDocuAi — the printed payment card, seeded so the review's editable
    // card fields start from what DocuAI extracted and save back on review.
    paymentCardNumber: documentInvoiceDetails.paymentCardNumber ?? null,
    paymentCardScheme: documentInvoiceDetails.paymentCardScheme ?? null,
    extractionConfidence: documentInvoiceDetails.extractionConfidence ?? null,
    // FLAG:useDocuAi — per-field confidence/source/validation for the review component
    // highlighting (graded validationState + note). Carried into the form data.
    fieldMeta: documentInvoiceDetails.fieldMeta ?? null,
    // FLAG:useDocuAi — which way the invoice points ('incoming' | 'outgoing' | 'unknown'),
    // derived server-side from the two parties vs the tenant's own company. DISPLAY-ONLY:
    // it only tells the payee section which roles can apply. 'unknown' (unmaintained
    // company data — the document-only case) keeps every role freely selectable.
    // FLAG:useDocuAi — the reviewer's persisted "expense or revenue?" decision. Read back so
    // a reopened review shows what was decided instead of falling back to the default; while nobody
    // has decided, this carries the PRESELECTION instead („Nur Dokument", see
    // resolveInitialBookingTarget). It stays `null` wherever the old default must survive — no
    // documents module, or an explicit intent that already named a ledger — and `null` is still NOT
    // the same as "expense" (see resolveReviewedDocumentTarget, which decides what a SAVE books).
    bookingTarget: resolveInitialBookingTarget(file, documentInvoiceDetails),
    documentDirection: ocr.documentDirection ?? 'unknown',
    // FLAG:useDocuAi — WHY the direction was decided ('taxNumber' | 'name' | 'nameAndZip' | null).
    // A tax-number match is an identifier and therefore strong enough to preselect the revenue
    // decision; a name/zip match is a namesake risk and stays a hint. Null on every document
    // analysed before this field existed, which reads as "no hard evidence" — the safe side.
    documentDirectionMatch: ocr.documentDirectionMatch ?? null,
    // FLAG:useDocuAi — die auf dem Beleg gedruckte USt-IdNr trägt MEHR ALS EIN Lieferant in den
    // Stammdaten (analyzeFile setzt den Marker). Dann identifiziert die stärkste Kennung niemanden
    // mehr und die Zuordnung fällt auf den Namen zurück — der Kontaktblock sagt das, statt den
    // Prüfer raten zu lassen, warum kein Lieferant zugeordnet wurde. Gemessen 19.09. auf Mandant
    // 65019: DE123456789 lag auf 97849 UND 97857, jeder Testbeleg landete beim ersten von beiden.
    // Reines Anzeigefeld, wird nicht gespeichert (die Save-Whitelist kennt es nicht).
    vatIdAmbiguous: !!ocr.payeeVatIdAmbiguous,
    // FLAG:useDocuAi — the two parties the document names, so the contact form can follow the
    // ROLE the reviewer picks. The payee snapshot starts as the vendor; switching to
    // Company/private customer used to keep showing that vendor, i.e. the wrong company entirely.
    // Read-only seeds: only a role switch ever copies one into the snapshot, and the reviewer's
    // own edits win (the section remembers what they had per party).
    recognizedParties: {
      supplier: buildNewSupplierReviewPayee(documentInvoiceDetails, ocr),
      buyer: buildRecognizedBuyerReviewPayee(ocr),
    },
    // FLAG:useDocuAi — the e-invoice verdict + format the analysis produced (serialize.js reads
    // it off the stored analysis result). `validationResult` is what the shared analyse-result
    // card already keys its "valid/invalid e-invoice" state on; `eInvoice` adds the format so
    // the card can name it. Both absent on a non-e-invoice → the card renders as before.
    validationResult: file?.validationResult ?? null,
    eInvoice: file?.eInvoice ?? null,
    // FLAG:useDocuAi — the document's own (non-system) tags, so the form's tag field shows what the
    // document actually carries instead of starting empty. The review save sends exactly these tags
    // back (merged with the „Art" system tag, see mergeReviewTags), which is what makes adding AND
    // removing a tag in the review stick. The „Art" itself is never offered for deletion here.
    tags: (file?.tags ?? [])
      .filter(tag => !tag?.isSystem)
      .map(tag => tag?.label)
      .filter(Boolean),
    metaData: {
      invoiceAnalysisSource: documentInvoiceDetails.reviewSource?.source ?? ocr.invoiceAnalysisSource ?? ocr.type,
      invoiceValuesReadonly: !!(documentInvoiceDetails.reviewSource?.invoiceValuesReadonly ?? ocr.invoiceValuesReadonly),
      documentType: documentInvoiceDetails.reviewSource?.documentType ?? ocr.documentType,
      invoiceFacts: documentInvoiceDetails.invoiceFacts ?? file?.metaData?.invoiceFacts ?? ocr.invoiceFacts,
    },
    readOnly: false,
    ...payeeDraft,
  }
}

export default buildReviewData
